/**
 * useSwap — orchestrates the full swap lifecycle:
 *
 *   1. Check ERC-20 allowance → prompt approval if needed
 *   2. Build calldata via backend
 *   3. Pre-flight simulate via eth_call
 *   4. Send transaction via wagmi
 *   5. Wait for on-chain confirmation
 *   6. Persist to history
 *
 * Extracted from SwapCard to keep the component focused on rendering.
 */

import { useState, useCallback, useEffect } from "react";
import {
  useAccount,
  useSendTransaction,
  useWaitForTransactionReceipt,
} from "wagmi";
import type { Address } from "viem";
import { directionString, type TokenSymbol } from "@/lib/tokens";
import { buildCalldata } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { useAllowance } from "./useAllowance";
import { useApprove } from "./useApprove";
import { useSimulate } from "./useSimulate";
import { useTxHistory } from "./useTxHistory";
import type { TxStep } from "@/components/TxStatus";

/** Type guard: is the value a non-empty hex string? */
function isHexHash(v: unknown): v is `0x${string}` {
  return typeof v === "string" && v.startsWith("0x") && v.length > 2;
}

interface UseSwapParams {
  inputToken: TokenSymbol;
  outputToken: TokenSymbol;
  tokenAddress: Address;
  amount: string;
  parsedAmount: bigint | null;
  slippageBps: number;
  expectedOut: string | undefined;
}

export function useSwap({
  inputToken,
  outputToken,
  tokenAddress,
  amount,
  parsedAmount,
  slippageBps,
  expectedOut,
}: UseSwapParams) {
  const [txStep, setTxStep] = useState<TxStep | null>(null);
  const [txError, setTxError] = useState<string | undefined>();
  const [swapTxHash, setSwapTxHash] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const { address } = useAccount();
  const { sendTransactionAsync } = useSendTransaction();

  // Allowance + approval
  const { allowance, needsApproval, refetch: refetchAllowance } =
    useAllowance(tokenAddress);
  const {
    state: approvalState,
    approve,
    txHash: approveTxHash,
    reset: resetApproval,
  } = useApprove(tokenAddress);

  // Simulation
  const { simulate } = useSimulate();

  // Transaction history
  const { entries: txHistoryEntries, addTx, updateStatus, clearHistory } =
    useTxHistory();

  // Swap tx receipt polling — only activate with a valid hash
  const validHash = isHexHash(swapTxHash) ? swapTxHash : undefined;
  const { isSuccess: swapConfirmed } = useWaitForTransactionReceipt({
    hash: validHash,
    query: { enabled: !!validHash },
  });

  // Sync swap receipt to step + update history
  useEffect(() => {
    if (swapConfirmed && txStep === "swap-pending") {
      setTxStep("success");
      if (swapTxHash) updateStatus(swapTxHash, "confirmed");
    }
  }, [swapConfirmed, txStep, swapTxHash, updateStatus]);

  // When approval confirms, refetch allowance so the button updates
  useEffect(() => {
    if (approvalState === "confirmed" && txStep === "approve-pending") {
      void refetchAllowance();
      resetApproval();
      setTxStep(null);
    }
  }, [approvalState, txStep, refetchAllowance, resetApproval]);

  const requiresApproval =
    parsedAmount !== null && parsedAmount > 0n && needsApproval(parsedAmount);

  // Approval action
  const doApprove = useCallback(async () => {
    setTxStep("approve-prompt");
    setTxError(undefined);
    try {
      await approve();
      setTxStep("approve-pending");
    } catch (err) {
      const msg = friendlyError(err);
      if (msg === "Transaction rejected in wallet.") {
        setTxStep(null);
      } else {
        setTxStep("error");
        setTxError(msg);
      }
    }
  }, [approve]);

  // Swap action
  const doSwap = useCallback(async () => {
    if (!address) return;

    setTxError(undefined);
    setSwapTxHash(undefined);
    setSubmitting(true);

    try {
      // Build calldata
      const built = await buildCalldata({
        amount,
        direction: directionString(inputToken),
        recipient: address,
        slippageBps,
        deadlineSecs: 120,
      });

      // Pre-flight simulation
      setTxStep("simulating");
      const simResult = await simulate(
        built.to as Address,
        built.data as `0x${string}`,
        address,
      );
      if (!simResult.ok) {
        setTxStep("error");
        setTxError(simResult.reason ?? "Simulation failed — transaction would revert.");
        setSubmitting(false);
        return;
      }

      // Prompt wallet
      setTxStep("swap-prompt");
      const hash = await sendTransactionAsync({
        to: built.to as Address,
        data: built.data as `0x${string}`,
        value: 0n,
      });
      setSwapTxHash(hash);
      setTxStep("swap-pending");

      // Persist to history
      addTx({
        hash,
        inputToken,
        outputToken,
        amountIn: amount,
        expectedOut: expectedOut ?? "—",
        status: "pending",
      });
    } catch (err) {
      const msg = friendlyError(err);
      if (msg === "Transaction rejected in wallet.") {
        setTxStep(null);
      } else {
        setTxStep("error");
        setTxError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    address, amount, inputToken, outputToken, slippageBps,
    sendTransactionAsync, simulate, addTx, expectedOut,
  ]);

  const closeTx = useCallback(() => {
    const wasSuccess = txStep === "success";
    setTxStep(null);
    setTxError(undefined);
    setSwapTxHash(undefined);
    return wasSuccess;
  }, [txStep]);

  return {
    // State
    txStep,
    txError,
    swapTxHash,
    approveTxHash,
    submitting,
    allowance,
    requiresApproval,
    approvalState,

    // Actions
    doApprove,
    doSwap,
    closeTx,
    resetApproval,

    // History
    txHistoryEntries,
    clearHistory,
  };
}
